import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '@/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, BookOpen, Clock, Tag } from 'lucide-react';
import { Badge } from "@/components/ui/badge";

export default function LessonReadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    async function loadLesson() {
      if (!auth.currentUser || !id) return;
      try {
        const uid = auth.currentUser.uid;
        const docRef = doc(db, 'teaching_workspaces', uid, 'lessons', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setData(snap.data());
        } else {
          console.error("Lesson not found");
        }
      } catch (err) {
        console.error("Error loading lesson:", err);
      } finally {
        setLoading(false);
      }
    }
    loadLesson();
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-4">
        <p className="text-muted-foreground text-lg">Lesson Plan not found.</p>
        <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
      </div>
    );
  }

  const isCbc = data.planType === 'cbc';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto w-full pb-20"
    >
      {/* Header */}
      <div className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/10 rounded-3xl p-8 mb-8 shadow-sm">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex flex-col">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              {data.title || 'Untitled Lesson'}
            </h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1"><BookOpen className="h-4 w-4" /> {data.subjectName}</span>
              <span className="flex items-center gap-1"><Tag className="h-4 w-4" /> {isCbc ? 'CBC Standard' : 'Universal Standard'}</span>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {data.lessonScope === 'topic' && data.selectedTopic && (
            <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
              Topic Specific
            </Badge>
          )}
          {data.lessonScope === 'subject' && (
            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              Subject Wide
            </Badge>
          )}
        </div>
      </div>

      {/* Content Body */}
      <div className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-sm space-y-10">
        {isCbc ? (
          <>
            <Section title="1. Sub-strand / Key Inquiry Questions">
              <p><strong>Strand:</strong> {data.cbcStrand}</p>
              <p><strong>Sub-Strand:</strong> {data.cbcSubStrand}</p>
              <div className="mt-2"><strong>Key Inquiry Questions:</strong> <div className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-400 mt-1">{data.cbcKiq}</div></div>
            </Section>
            <Section title="2. Specific Learning Outcomes">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><strong className="text-indigo-600 dark:text-indigo-400">Knowledge</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcKnowledge}</div></div>
                <div><strong className="text-indigo-600 dark:text-indigo-400">Skills</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcSkill}</div></div>
                <div><strong className="text-indigo-600 dark:text-indigo-400">Attitudes</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcAttitude}</div></div>
              </div>
            </Section>
            <Section title="3. Learning Resources">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><strong>Textbooks & Refs:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcTextbook}</div></div>
                <div><strong>Realia/Materials:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcRealia}</div></div>
                <div><strong>Digital Resources:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcDigital}</div></div>
              </div>
            </Section>
            <Section title="4. Learning Experiences (The Lesson)">
              <div className="space-y-4">
                <div><strong>Introduction:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcIntro}</div></div>
                <div><strong>Lesson Development:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcDevelopment}</div></div>
                <div><strong>Conclusion:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcConclusionPhase}</div></div>
              </div>
            </Section>
            <Section title="5. Core Competencies & Integration">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><strong>Competencies:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcCompetencies}</div></div>
                <div><strong>PCIs:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcPci}</div></div>
                <div><strong>Values:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcValues}</div></div>
              </div>
            </Section>
            <Section title="6. Assessment & Inclusion">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><strong>Assessment Methods:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcAssessmentMethod}</div></div>
                <div><strong>Rubrics:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcRubric}</div></div>
                <div><strong>Differentiation:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcDifferentiation}</div></div>
              </div>
            </Section>
            <Section title="7. Lesson Reflection">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><strong>What Went Well:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcWhatWentWell}</div></div>
                <div><strong>What Didn't Work:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcWhatDidnt}</div></div>
                <div><strong>Follow-up Plan:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcFollowUp}</div></div>
                <div><strong>Remarks:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.cbcRemarks}</div></div>
              </div>
            </Section>
          </>
        ) : (
          <>
            <Section title="1. Goals & Materials">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><strong>Learning Outcomes:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.outcomes}</div></div>
                <div><strong>Materials Required:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univMaterials}</div></div>
              </div>
            </Section>
            <Section title="2. Lesson Structure (I Do, We Do, You Do)">
              <div className="space-y-4">
                <div><strong>Anticipatory Set (Hook):</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univAnticipatory}</div></div>
                <div><strong>Direct Instruction (I Do):</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univIDo}</div></div>
                <div><strong>Guided Practice (We Do):</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univWeDo}</div></div>
                <div><strong>Independent Practice (You Do):</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univYouDo}</div></div>
              </div>
            </Section>
            <Section title="3. Support & Assessment">
              <div className="space-y-4">
                <div><strong>Differentiation:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univDifferentiation}</div></div>
                <div><strong>Formative Assessment:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univFormative}</div></div>
                <div><strong>Summative Assessment:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univSummative}</div></div>
                <div><strong>Closure:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.univClosure}</div></div>
              </div>
            </Section>
            <Section title="4. Reflection">
              <div><strong>Self-Reflection:</strong><div className="whitespace-pre-wrap text-sm mt-1">{data.reflection}</div></div>
            </Section>
          </>
        )}
      </div>
    </motion.div>
  );
}

function Section({ title, children }: { title: string, children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 pb-2 border-b border-slate-100 dark:border-white/5">{title}</h3>
      <div className="text-slate-700 dark:text-slate-300">
        {children}
      </div>
    </div>
  );
}
